/**
 * Public site layout with footer only (no header).
 *
 * Features:
 * - No header — clean reading experience
 * - Sign In / user menu in the footer
 * - Navigation links in the footer
 * - Dark theme matching the site aesthetic
 *
 * Called by:
 * - App.tsx as wrapper for public routes
 */
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Button, Dropdown, Avatar, Typography } from 'antd';
import { UserOutlined, LogoutOutlined, SettingOutlined, LoginOutlined } from '@ant-design/icons';
import { useAuth } from '../hooks/useAuth';
import ReportIssueButton from './ReportIssueButton';
import { usePageTracking } from '../hooks/usePageTracking';
import { getLoginUrl } from '../services/api';

const { Content, Footer } = Layout;
const { Text } = Typography;

function PublicLayout() {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  usePageTracking();

  async function handleLogout() {
    await logout();
    navigate('/');
  }

  function handleLogin() {
    const returnTo = encodeURIComponent(location.pathname + location.search);
    window.location.href = `${getLoginUrl()}?returnTo=${returnTo}`;
  }

  const userMenuItems = [
    {
      key: 'info',
      label: (
        <div style={{ padding: '8px 0' }}>
          <Text strong>{user?.name}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {user?.email}
          </Text>
        </div>
      ),
      disabled: true,
    },
    { type: 'divider' as const },
    ...(user?.role === 'author' || user?.role === 'editor' ? [{
      key: 'admin',
      icon: <SettingOutlined />,
      label: 'Reviewer Portal',
      onClick: () => navigate('/reviewer'),
    }] : []),
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Log out',
      onClick: handleLogout,
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh', background: '#0a0a0f' }}>
      {/* Main content */}
      <Content style={{ padding: '48px 48px 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
          <Outlet />
        </div>
      </Content>

      {/* Footer */}
      <Footer
        style={{
          background: 'rgba(10, 10, 15, 0.95)',
          borderTop: '1px solid rgba(255, 255, 255, 0.08)',
          padding: '16px 48px',
          marginTop: '64px',
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link
            to="/"
            style={{
              color: '#f5f5f5',
              fontFamily: "var(--font-serif)",
              fontSize: '1.1rem',
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <img src="/logo-wide.png" alt="Compendium" style={{ height: 28 }} />
          </Link>

          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <ReportIssueButton variant="footer" />
          {isAuthenticated ? (
            <Dropdown menu={{ items: userMenuItems }} trigger={['click']} placement="topRight">
              <Button
                type="text"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  height: 'auto',
                  padding: '4px 12px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '20px',
                }}
              >
                <Avatar
                  src={user?.avatarUrl}
                  icon={!user?.avatarUrl && <UserOutlined />}
                  size="small"
                />
                <Text style={{ color: '#f5f5f5' }}>{user?.name?.split(' ')[0]}</Text>
              </Button>
            </Dropdown>
          ) : (
            <Button
              type="primary"
              icon={<LoginOutlined />}
              onClick={handleLogin}
              style={{
                background: 'linear-gradient(135deg, #d4af37 0%, #b8962e 100%)',
                border: 'none',
                borderRadius: '20px',
                fontWeight: 500,
              }}
            >
              Sign In
            </Button>
          )}
          </div>
        </div>
      </Footer>
    </Layout>
  );
}

export default PublicLayout;
